import { z } from 'zod'

import { http } from '@/shared/api'

import { getMustChangePassword } from '../model/credential-store'
import {
  PERMISSION_ADMIN,
  permissionsForRole,
  type Session,
  type Subscription,
} from '../model/session'
import { ensureAuthReady, isAuthDisabled, isSignedIn } from '../model/session-lifecycle'

import { goauthHttp } from './goauth-http'

/*
 * The session is two answers, from the two services that own them.
 *
 * Who the reader is — goauth's, which is also where the panel signs them in and
 * where an administrator edits them. api-service used to answer this from its
 * mirror, and a mirror is a copy: it trails goauth by however long a webhook
 * takes, so the panel could show a name or a role that had already changed.
 *
 * What they have bought — ours, and goauth knows nothing about it.
 */

/** goauth's `/auth/me`. `is_admin`, not a role, and no subscription anywhere. */
const identitySchema = z.object({
  id: z.string(),
  username: z.string().nullish(),
  email: z.string().nullish(),
  is_admin: z.boolean(),
})

/**
 * api-service's `/subscription`.
 *
 * Two slots, both nullable: what is running now and what has been paid for and
 * has not started. `null` in either is an answer, not a gap.
 *
 * `max_campaigns` and `days_left` are read from here rather than worked out on
 * the client. The ceiling is the same number the server enforces, and the
 * countdown is rounded up by the same code that decides when a period ends —
 * two clocks would disagree by a day around midnight.
 */
const planViewSchema = z.object({
  status: z.string(),
  plan_slug: z.string(),
  plan_name_ru: z.string(),
  plan_name_en: z.string(),
  tier: z.number(),
  max_campaigns: z.number().nullable(),
  days_left: z.number(),
  period_end: z.string(),
})

const subscriptionSchema = z.object({
  active: planViewSchema.nullable(),
  scheduled: planViewSchema.nullable(),
})

const toSubscription = (view: z.infer<typeof planViewSchema> | null): Subscription | null =>
  view === null
    ? null
    : {
        status: view.status,
        planSlug: view.plan_slug,
        planNameRu: view.plan_name_ru,
        planNameEn: view.plan_name_en,
        tier: view.tier,
        maxCampaigns: view.max_campaigns,
        daysLeft: view.days_left,
        currentPeriodEnd: view.period_end,
      }

/** Renders the panel without a backend. Development builds only. */
const DEV_SESSION: Session = {
  id: 'dev',
  name: 'Developer',
  email: 'dev@localhost',
  role: 'super_admin',
  permissions: [PERMISSION_ADMIN],
  subscription: null,
  scheduledSubscription: null,
  mustChangePassword: false,
}

export async function fetchSession(signal?: AbortSignal): Promise<Session | null> {
  if (isAuthDisabled) return DEV_SESSION

  await ensureAuthReady()

  // No credential means "not signed in" — a real answer. Asking either service
  // anyway would 401, trip the refresh-then-sessionLost path, and `runResets()`
  // would clear the query cache from underneath the guard still awaiting it.
  if (!isSignedIn()) return null

  const [identity, subscription] = await Promise.all([
    goauthHttp.get('auth/me', { schema: identitySchema, signal }),
    http.get('subscription', { schema: subscriptionSchema, signal }),
  ])

  return {
    id: identity.id,
    // The login, or the address when there is none — goauth clears both on a
    // soft delete, and then the id is all that is left to call someone.
    name: identity.username ?? identity.email ?? identity.id,
    email: identity.email ?? '',
    role: identity.is_admin ? 'super_admin' : 'user',
    permissions: permissionsForRole(identity.is_admin ? 'super_admin' : 'user'),
    // A property of the grant rather than of the person: goauth reports it with
    // the token and not on `/auth/me`.
    mustChangePassword: getMustChangePassword(),
    subscription: toSubscription(subscription.active),
    scheduledSubscription: toSubscription(subscription.scheduled),
  }
}
